import { Component, Input, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { finalize } from "rxjs";
import { AlertService } from "@modules/alert";
import { UserData } from "src/app/_models/user.model";
import {
  ClientAccountManagersData,
  ClientsService,
} from "src/app/_services/clients.service";
import { LoadingTypeEnum } from "../../shared/_enums/loading-type.enum";
import { CompanyAccountManagersDialogComponent } from "../company-account-managers-dialog/company-account-managers-dialog.component";

interface AccountManagerItem {
  id: number;
  label: string;
}

@Component({
  selector: "app-company-account-managers",
  templateUrl: "./company-account-managers.component.html",
  styleUrls: ["./company-account-managers.component.scss"],
})
export class CompanyAccountManagersComponent implements OnInit {
  @Input() entityId: number;

  public isLoading = false;
  public isUpdating = false;
  public accountManagers: AccountManagerItem[] = [];
  public accountManagerIds: number[] = [];

  constructor(
    private clientsService: ClientsService,
    private alertService: AlertService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  public loadData(): void {
    this.isLoading = true;

    this.clientsService
      .getClientAccountManagers(this.entityId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response) => {
          this.setAccountManagersData(response.result ?? null);
        },
        error: () => {
          this.accountManagers = [];
          this.accountManagerIds = [];
        },
      });
  }

  public openAddDialog(): void {
    const dialogRef = this.dialog.open(CompanyAccountManagersDialogComponent, {
      width: "720px",
      data: {
        excludeIds: this.accountManagerIds,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((selectedAdminIds: number[] | undefined) => {
        if (!Array.isArray(selectedAdminIds) || selectedAdminIds.length === 0) {
          return;
        }

        this.persistAccountManagers([
          ...this.accountManagerIds,
          ...selectedAdminIds,
        ]);
      });
  }

  public removeAccountManager(accountManagerId: number): void {
    if (this.isUpdating) {
      return;
    }

    this.persistAccountManagers(
      this.accountManagerIds.filter((id) => id !== accountManagerId)
    );
  }

  public get canOpenDialog(): boolean {
    return !this.isLoading && !this.isUpdating;
  }

  private setAccountManagersData(
    accountManagerData: ClientAccountManagersData | null
  ): void {
    this.accountManagerIds = this.normalizeIds(
      accountManagerData?.account_manager_ids
    );
    this.accountManagers = [...(accountManagerData?.account_managers ?? [])]
      .map((user) => ({
        id: user.id,
        label: this.getAdminLabel(user),
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  private getAdminLabel(admin: Partial<UserData>): string {
    const name =
      admin.name?.trim() ||
      [admin.first_name, admin.last_name].filter(Boolean).join(" ").trim();

    if (name && admin.email) {
      return `${name} (${admin.email})`;
    }

    return name || admin.email || `#${admin.id}`;
  }

  private persistAccountManagers(accountManagerIds: number[]): void {
    this.isUpdating = true;

    this.clientsService
      .updateClientAccountManagers(
        this.entityId,
        this.normalizeIds(accountManagerIds)
      )
      .pipe(finalize(() => (this.isUpdating = false)))
      .subscribe({
        next: (response) => {
          this.setAccountManagersData(response.result ?? null);
          this.alertService.saved();
        },
        error: (err) => this.handleError(err),
      });
  }

  private normalizeIds(ids: number[] | null | undefined): number[] {
    return Array.from(
      new Set(
        [...(ids ?? [])]
          .map((id) => Number(id))
          .filter((id) => !Number.isNaN(id))
      )
    ).sort((left, right) => left - right);
  }

  private handleError(err: any): void {
    if (err?.error?.message) {
      this.alertService.stringError(err.error.message);
    } else {
      this.alertService.errors(err?.error?.errors);
    }
  }

  get LoadingType() {
    return LoadingTypeEnum;
  }
}
